import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Phone, Mail, Briefcase, User, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const roleColors = {
  Owner: 'bg-amber-100 text-amber-700',
  'Top Management': 'bg-purple-100 text-purple-700',
  Management: 'bg-indigo-100 text-indigo-700',
  Manager: 'bg-blue-100 text-blue-700',
  Staff: 'bg-gray-100 text-gray-600',
};

const MemberCard = ({ member }) => (
  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`team-member-${member.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        {member.photo_url ? (
          <img src={member.photo_url.startsWith('/') ? `${API}${member.photo_url}` : member.photo_url} alt="" className="w-11 h-11 rounded-full object-cover border border-gray-200 flex-shrink-0" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-sky-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900 truncate">{member.name}</p>
            <Badge className={`text-[9px] px-1.5 py-0 ${roleColors[member.employee_role] || roleColors.Staff}`}>
              {member.employee_role || 'Staff'}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Briefcase className="h-3 w-3 text-gray-300" />
            <p className="text-xs text-gray-500">{member.position || 'N/A'}</p>
          </div>
          <div className="flex items-center gap-3 mt-2">
            {member.phone && (
              <a href={`tel:${member.phone}`} className="flex items-center gap-1 text-[10px] text-sky-600">
                <Phone className="h-3 w-3" /> {member.phone}
              </a>
            )}
            {member.email && (
              <a href={`mailto:${member.email}`} className="flex items-center gap-1 text-[10px] text-sky-600 underline">
                <Mail className="h-3 w-3" /> {member.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const PortalMyTeam = () => {
  const [team, setTeam] = useState([]);
  const [supervised, setSupervised] = useState([]);
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/employee-portal/my-team`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeam(res.data.members || []);
        setSupervised(res.data.supervised || []);
        setDepartment(res.data.department || '');
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('portal_token');
          navigate('/employee-portal/login');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  // Group supervised staff by department
  const supervisedByDept = {};
  supervised.forEach(m => {
    const dept = m.department || 'Other';
    if (!supervisedByDept[dept]) supervisedByDept[dept] = [];
    supervisedByDept[dept].push(m);
  });

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-my-team">
      {/* Department Team Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
          <Users className="h-5 w-5 text-sky-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">My Team</h2>
          <p className="text-xs text-gray-400">{department} Department &middot; {team.length} member{team.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Department Members */}
      {team.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-8 text-center">
            <Users className="h-10 w-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No other team members in {department}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {team.map(member => <MemberCard key={member.id} member={member} />)}
        </div>
      )}

      {/* Supervised Staff */}
      {supervised.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Supervised Staff</h2>
              <p className="text-xs text-gray-400">{supervised.length} direct report{supervised.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {Object.entries(supervisedByDept).map(([dept, members]) => (
            <div key={dept}>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 px-1">{dept}</p>
              <div className="space-y-2">
                {members.map(member => <MemberCard key={member.id} member={member} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalMyTeam;
